/* Behaviour for sections/cowmit-bundle-header.liquid.
 *
 * Deliberately standalone: this page renders through layout/page.cowmit-bundle.liquid, which does
 * NOT load the theme's header bundle, so header-drawer / sticky-header / details-modal are not
 * defined here. Everything the header needs is below, and nothing here touches the theme's own
 * header on other pages.
 *
 * The cart icon is NOT wired here on purpose. It keeps the theme's id="cart-icon-bubble", which the
 * theme's cart-drawer element finds and binds itself, and which assets/cowmit-bundle.js refreshes
 * through the Section Rendering API. Adding a handler here would double-open the drawer.
 */
(function () {
  'use strict';

  var DESKTOP = 990; // the theme's own breakpoint, matched in cowmit-bundle-header.css

  function init(root) {
    if (!root || root.dataset.cbhReady === 'true') return;
    root.dataset.cbhReady = 'true';

    var header = root.querySelector('[data-cbh-hdr]');
    var panelScrim = root.querySelector('[data-cbh-panel-scrim]');
    var burger = root.querySelector('[data-cbh-burger]');

    // The drawer and its scrim are siblings of .cbh, not children: they are position:fixed, and a
    // sticky ancestor that gains a transform would trap them.
    var drawer = document.querySelector('[data-cbh-drawer]');
    var scrim = document.querySelector('[data-cbh-scrim]');
    var drawerClose = drawer ? drawer.querySelector('[data-cbh-drawer-close]') : null;

    /* ---------------- dropdown panels ("Alle smaken", "Over Cowmit") ----------------
     * Written as a LIST, not as one hard-coded pair, so a second dropdown is a markup change and
     * not a second copy of this logic. The two would drift apart the moment either was edited -
     * the same reason sections/cowmit-bundle-header-group.json was retired rather than kept
     * alongside header-group.json.
     *
     * A trigger is paired with its panel through aria-controls, which the markup already needs for
     * assistive tech, so the relationship is declared once and not duplicated in a data attribute.
     */

    var panels = [];

    (function collectPanels() {
      var btns = root.querySelectorAll('[data-cbh-panel-btn]');
      for (var i = 0; i < btns.length; i++) {
        var btn = btns[i];
        var id = btn.getAttribute('aria-controls');
        var el = id ? root.querySelector('#' + id) : null;
        if (!el) continue;
        panels.push({ btn: btn, el: el, card: el.querySelector('[data-cbh-panel-card]') });
      }
    })();

    function openPanel() {
      for (var i = 0; i < panels.length; i++) {
        if (panels[i].btn.getAttribute('aria-expanded') === 'true') return panels[i];
      }
      return null;
    }

    function panelIsOpen() {
      return !!openPanel();
    }

    function positionTail(p) {
      if (!p || !p.btn || !p.card) return;
      var trigger = p.btn.getBoundingClientRect();
      var card = p.card.getBoundingClientRect();
      // Measured from the live trigger each time rather than hardcoded, so the pointer stays under
      // its own label whatever the label, the logo width or the viewport.
      p.card.style.setProperty(
        '--cbh-tail-x',
        Math.round(trigger.left + trigger.width / 2 - card.left) + 'px'
      );
    }

    function setPanelState(p, open) {
      if (!p) return;
      if (open) {
        // Only one panel at a time: two open dropdowns would overlap, and both would sit over the
        // same scrim.
        for (var i = 0; i < panels.length; i++) {
          if (panels[i] !== p) setPanelState(panels[i], false);
        }
        positionTail(p);
      }
      p.el.classList.toggle('is-open', open);
      p.btn.setAttribute('aria-expanded', String(open));
      if (panelScrim) panelScrim.classList.toggle('is-open', panelIsOpen());
    }

    // Kept for the call sites that just want "close whatever is open" (Escape, drawer opening).
    function setPanel(open) {
      if (open) {
        if (panels.length) setPanelState(panels[0], true);
        return;
      }
      for (var i = 0; i < panels.length; i++) setPanelState(panels[i], false);
    }

    if (panels.length) {
      for (var pi = 0; pi < panels.length; pi++) {
        (function (p) {
          p.btn.addEventListener('click', function (event) {
            event.stopPropagation();
            setPanelState(p, p.btn.getAttribute('aria-expanded') !== 'true');
          });
        })(panels[pi]);
      }

      if (panelScrim) {
        panelScrim.addEventListener('click', function () {
          setPanel(false);
        });
      }

      document.addEventListener('click', function (event) {
        var p = openPanel();
        if (!p) return;
        if (p.el.contains(event.target) || p.btn.contains(event.target)) return;
        setPanelState(p, false);
      });

      // Keep the pointer under the trigger if the window is resized while a panel is open.
      // Also close a panel whose trigger has just been hidden by a breakpoint (the left cluster
      // goes at 1199, the right-hand link at 749): the panel would otherwise stay open over the
      // page with no control left to close it but the scrim.
      window.addEventListener(
        'resize',
        function () {
          var p = openPanel();
          if (!p) return;
          if (!p.btn.getClientRects().length) {
            setPanelState(p, false);
            return;
          }
          positionTail(p);
        },
        { passive: true }
      );
    }

    /* ---------------- mobile drawer ---------------- */

    var scrollLock = null;

    function drawerIsOpen() {
      return !!drawer && drawer.classList.contains('is-open');
    }

    function setDrawer(open) {
      if (!drawer) return;
      drawer.classList.toggle('is-open', open);
      if (scrim) scrim.classList.toggle('is-open', open);
      if (burger) burger.setAttribute('aria-expanded', String(open));

      if (open) {
        // Stored and restored rather than blanked: the page may already be locked by the cart
        // drawer, and clearing it here would silently unlock that one.
        scrollLock = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        drawer.focus();
      } else {
        document.documentElement.style.overflow = scrollLock === null ? '' : scrollLock;
        scrollLock = null;
        if (burger) burger.focus();
      }
    }

    if (burger) {
      burger.addEventListener('click', function () {
        setDrawer(!drawerIsOpen());
      });
    }
    if (drawerClose) {
      drawerClose.addEventListener('click', function () {
        setDrawer(false);
      });
    }
    if (scrim) {
      scrim.addEventListener('click', function () {
        setDrawer(false);
      });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      if (panelIsOpen()) setPanel(false);
      if (drawerIsOpen()) setDrawer(false);
    });

    // Keep focus inside the drawer while it is open: it is aria-modal, so leaving focus behind it
    // would let a keyboard user tab into content they cannot see.
    if (drawer) {
      drawer.addEventListener('keydown', function (event) {
        if (event.key !== 'Tab' || !drawerIsOpen()) return;
        var focusable = drawer.querySelectorAll('a[href], button:not([disabled])');
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    }

    // Crossing the breakpoint with the drawer open would leave the page scroll-locked behind a
    // desktop layout that hides the drawer.
    window.addEventListener(
      'resize',
      function () {
        if (window.innerWidth >= DESKTOP && drawerIsOpen()) setDrawer(false);
      },
      { passive: true }
    );

    /* ---------------- pinned header, "on scroll up" ---------------- */
    // Matches the store's sticky_header_type setting. Implemented here rather than reusing the
    // theme's <sticky-header>, which is defined only in the remote bundle this layout does not load.
    //
    // position:fixed with a spacer, NOT position:sticky. Sticky only holds an element while its own
    // parent is still on screen, and the header's parents (.cbh and the section's wrapper div) are
    // no taller than the header itself - a sticky bar therefore scrolled away on the first swipe
    // and never came back. Pinning past a measured threshold is what actually reproduces the live
    // behaviour.

    var spacer = root.querySelector('[data-cbh-spacer]');

    if (header && spacer && root.dataset.sticky === 'true') {
      var lastY = window.pageYOffset;
      var ticking = false;
      var pinned = false;

      // Measured, not assumed: the bar is 70px at desktop and 62px on mobile, and a long
      // announcement can wrap. Read from the DOM while the bar is still in normal flow.
      var barHeight = function () {
        return header.getBoundingClientRect().height;
      };

      // The document offset the bar has to clear before it is allowed to pin. Read while unpinned,
      // because once fixed the bar reports its viewport position instead.
      //
      // Measured off the HEADER, not off the spacer. The spacer is display:none until it is
      // activated, and getBoundingClientRect() on a display:none element is all zeros - so this
      // returned 0 on every page, and the announcement bar's height was silently dropped from the
      // threshold. The bar then pinned one announcement-height early and, worse, only unpinned at
      // exactly scrollY 0: coming back up, the fixed bar sat at viewport 0 while the unpinned bar
      // belongs at 38, so the last frame of the scroll jumped the header by that much. The header
      // is in normal flow while unpinned (and, on the transparent home page, absolutely placed at
      // its own static position), so its own offset is the number that was always meant.
      var pinPoint = function () {
        return header.getBoundingClientRect().top + window.pageYOffset;
      };

      var threshold = pinPoint();

      var setPinned = function (next) {
        if (next === pinned) return;
        pinned = next;
        if (next) {
          // Height captured BEFORE the bar leaves the flow, or it measures zero.
          spacer.style.height = barHeight() + 'px';
          spacer.classList.add('is-active');
          header.classList.add('cbh-hdr--pinned');
        } else {
          header.classList.remove('cbh-hdr--pinned', 'cbh-hdr--hidden');
          spacer.classList.remove('is-active');
          spacer.style.height = '';
        }
      };

      var onScroll = function () {
        ticking = false;
        var y = window.pageYOffset;

        if (!pinned) {
          // Self-healing: while the bar is still in flow its document offset is constant at any
          // scroll position, so re-reading it costs one rect and survives anything that shifts the
          // announcement bar after load - a web font, a longer message, an app block.
          threshold = pinPoint();
        }

        if (!pinned && y > threshold + barHeight()) {
          // Pinned while the page is still moving down, so it arrives already off-screen and slides
          // in on the way back up instead of flashing over the content.
          //
          // The transition has to be suppressed for exactly this one frame. .cbh-hdr--pinned brings
          // `transition: transform 260ms` with it, and the bar's transform up to now was `none`, so
          // adding both classes together animated the SOLID bar down from the top of the viewport
          // and back up - a cream flash over the hero on every downward crossing. Forcing a
          // synchronous style flush (offsetHeight) while transitions are off makes translateY(-100%)
          // the starting value rather than the end of an animation; the class comes straight back
          // off so the later show/hide still animates.
          header.classList.add('cbh-hdr--nofx');
          setPinned(true);
          header.classList.add('cbh-hdr--hidden');
          void header.offsetHeight;
          header.classList.remove('cbh-hdr--nofx');
          lastY = y;
          return;
        }

        if (pinned && y <= threshold) {
          setPinned(false);
          lastY = y;
          return;
        }

        if (pinned) {
          // Never hide the bar out from under something anchored to it.
          if (panelIsOpen() || drawerIsOpen()) {
            header.classList.remove('cbh-hdr--hidden');
          } else if (y > lastY + 4) {
            header.classList.add('cbh-hdr--hidden');
          } else if (y < lastY - 4) {
            header.classList.remove('cbh-hdr--hidden');
          }
        }

        lastY = y;
      };

      window.addEventListener(
        'scroll',
        function () {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(onScroll);
        },
        { passive: true }
      );

      window.addEventListener(
        'resize',
        function () {
          if (pinned) {
            spacer.style.height = barHeight() + 'px';
          } else {
            threshold = pinPoint();
          }
        },
        { passive: true }
      );
    }

    /* ---------------- transparent over the hero ---------------- */
    // The bar is transparent only while it is still over the home page's FIRST section - the hero -
    // and solid from the moment its bottom edge clears that section. Driven separately from the
    // pinned state on purpose: keying the solid look off --pinned meant any scroll-up inside the
    // hero produced a cream bar sitting on the hero photo, and every crossing of the pin point
    // repainted the whole bar. Now the colour changes exactly once in each direction, at the hero's
    // bottom edge, and the CSS fades it over 220ms.

    if (header && root.classList.contains('cbh--transparent')) {
      // layout/theme.liquid wraps the template's sections in <main id="MainContent">, so the hero
      // is that element's first child. Verified against the live home page, whose first section is
      // shopify-section-...__cowmit_hero_... . No merchant setting for this: a CSS selector in the
      // schema is a support burden, and a template whose first section is not the hero would want a
      // different design anyway.
      var mainEl = document.getElementById('MainContent') || document.querySelector('main');
      var hero = mainEl ? mainEl.firstElementChild : null;
      var solidTicking = false;

      var syncSolid = function () {
        solidTicking = false;

        if (!hero) {
          // Nothing to measure against - fall back to the old rule rather than to "always solid",
          // which would silently turn the whole feature off.
          header.classList.toggle('cbh-hdr--solid', header.classList.contains('cbh-hdr--pinned'));
          return;
        }

        // The bar's bottom edge as it would sit if it were fully shown. While pinned that is its
        // own height, because it is fixed at top:0. Its LIVE rect is wrong here: --hidden translates
        // it fully out of view, so the rect bottom goes negative and every hidden bar would read as
        // still over the hero - it would then slide back in transparent over mid-page content.
        var edge = header.classList.contains('cbh-hdr--pinned')
          ? header.getBoundingClientRect().height
          : header.getBoundingClientRect().bottom;

        header.classList.toggle('cbh-hdr--solid', hero.getBoundingClientRect().bottom <= edge);
      };

      syncSolid();

      window.addEventListener(
        'scroll',
        function () {
          if (solidTicking) return;
          solidTicking = true;
          // Queued AFTER the pinning listener above, which is registered first, so --pinned is
          // already up to date for this frame when this runs.
          window.requestAnimationFrame(syncSolid);
        },
        { passive: true }
      );

      window.addEventListener('resize', syncSolid, { passive: true });
    }

    /* ---------------- theme editor ---------------- */
    // Selecting a block in the editor should reveal it, or the merchant edits a card they cannot
    // see. The panel is resolved from the selected block itself rather than defaulting to the first
    // one, so selecting a Story block opens the story panel and not "Alle smaken".
    function panelForNode(node) {
      for (var i = 0; i < panels.length; i++) {
        if (panels[i].el.contains(node)) return panels[i];
      }
      return null;
    }

    document.addEventListener('shopify:block:select', function (event) {
      if (!root.contains(event.target)) return;
      var p = panelForNode(event.target);
      if (p) setPanelState(p, true);
    });
    document.addEventListener('shopify:block:deselect', function (event) {
      if (!root.contains(event.target)) return;
      var p = panelForNode(event.target);
      if (p) setPanelState(p, false);
    });
  }

  function boot() {
    var roots = document.querySelectorAll('[data-cbh]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // The editor replaces the section's markup wholesale on every settings change, which drops all of
  // the listeners above along with it.
  document.addEventListener('shopify:section:load', boot);
})();
